/**
 * Menu inicial — vibe NFS (cinemático/neon) + Mario Kart (energia arcade).
 * Música: tenta /menu/music/intro.mp3; se não houver, gera um loop arcade com Web Audio.
 * Pular intro / mute / volume ficam disponíveis.
 */
export default class MainMenu {
    constructor({ onPlay, onSolo } = {}) {
        this.onPlay = onPlay || (() => {})
        this.onSolo = onSolo || (() => {})
        this.audioCtx = null
        this.musicGain = null
        this.htmlAudio = null
        this.proceduralNodes = []
        this.muted = false
        this.volume = 0.55
        this.playing = false
        this.phase = 'boot' // boot | menu | leaving
        this.root = null
        this._raf = 0
        this._build()
        this._bind()
        // tenta autoplay; navegadores modernos exigem gesto — botão "TOCAR MÚSICA" aparece se falhar
        this._tryStartMusic()
    }

    _build() {
        const root = document.createElement('div')
        root.id = 'main-menu'
        root.innerHTML = `
            <div class="mm-bg">
                <div class="mm-scan"></div>
                <div class="mm-grid"></div>
                <div class="mm-glow mm-glow-a"></div>
                <div class="mm-glow mm-glow-b"></div>
                <div class="mm-speedlines"></div>
            </div>
            <div class="mm-content">
                <div class="mm-topline"><span class="mm-badge"><i></i> CARPANO RACING CLUB</span><span class="mm-season">SEASON 01 <b>● LIVE</b></span></div>
                <div class="mm-hero-layout">
                    <div class="mm-copy">
                        <p class="mm-eyebrow">SUA RUA. SUAS REGRAS.</p>
                        <h1 class="mm-logo">
                            <span class="mm-logo-nfs">CARPANO</span>
                            <span class="mm-logo-kart">KART<span class="mm-logo-slash">///</span></span>
                        </h1>
                        <p class="mm-tag">Pise fundo. Monte sua equipe. Faça história na pista.</p>
                        <div class="mm-specs"><span><b>01</b> ESCOLHA SEU PILOTO</span><span><b>02</b> MONTE SEU CARRO</span><span><b>03</b> DOMINE A PISTA</span></div>
                    </div>
                    <div class="mm-showcase" aria-hidden="true">
                        <div class="mm-showcase-orbit"></div><div class="mm-showcase-light"></div>
                        <div class="mm-car-art"><span class="mm-car-wing"></span><span class="mm-car-window"></span><span class="mm-car-wheel mm-wheel-a"></span><span class="mm-car-wheel mm-wheel-b"></span><span class="mm-car-stripe"></span></div>
                        <div class="mm-showcase-caption"><span>CARPANO <b>GT-R</b></span><small>BUILT FOR THE NIGHT</small></div>
                        <div class="mm-showcase-index">01 <i>/</i> 03</div>
                    </div>
                </div>

                <div class="mm-actions">
                    <button type="button" class="mm-btn mm-btn-primary" data-act="play">
                        <span class="mm-btn-icon">🏁</span>
                        JOGAR ONLINE <span class="mm-btn-arrow">↗</span>
                    </button>
                    <button type="button" class="mm-btn mm-btn-ghost" data-act="solo">
                        <span class="mm-btn-icon">🚗</span>
                        CORRIDA SOLO
                    </button>
                </div>

                <div class="mm-audio-bar">
                    <button type="button" class="mm-audio-btn" data-act="music-toggle" title="Música">
                        <span class="mm-music-icon">♪</span>
                        <span class="mm-music-label">Música</span>
                    </button>
                    <input type="range" class="mm-vol" min="0" max="100" value="55" aria-label="Volume">
                    <button type="button" class="mm-audio-btn mm-skip" data-act="skip" title="Pular intro">
                        PULAR INTRO ▸▸
                    </button>
                </div>

                <p class="mm-hint"><span class="mm-key">ENTER</span> JOGAR <span class="mm-hint-dot">·</span> <span class="mm-key">M</span> MÚSICA <span class="mm-hint-dot">·</span> FEITO PARA CORRER JUNTO</p>
            </div>
            <div class="mm-footer"><span>© CARPANO KART</span><span>ARCADE STREET RACING <i>◆</i> MULTIPLAYER READY</span><span>POWERED BY CARPANO</span></div>
        `
        document.body.appendChild(root)
        this.root = root
        this.volSlider = root.querySelector('.mm-vol')
        this.musicLabel = root.querySelector('.mm-music-label')
        this.musicIcon = root.querySelector('.mm-music-icon')
    }

    _bind() {
        this.root.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-act]')
            if (!btn) return
            const act = btn.dataset.act
            if (act === 'play') this._leave(() => this.onPlay())
            else if (act === 'solo') this._leave(() => this.onSolo())
            else if (act === 'music-toggle') this.toggleMute()
            else if (act === 'skip') this.skipIntro()
        })
        this.volSlider.addEventListener('input', () => {
            this.volume = Number(this.volSlider.value) / 100
            this._applyVolume()
            if (this.muted && this.volume > 0) {
                this.muted = false
                this._updateMusicUi()
            }
            // gesto válido: se o autoplay foi bloqueado antes, a música começa aqui
            if (!this.playing && !this.muted) this._tryStartMusic()
        })
        // qualquer tecla de atalho
        this._onKey = (e) => {
            if (this.phase !== 'menu' && this.phase !== 'boot') return
            if (e.code === 'Enter' || e.code === 'Space') {
                e.preventDefault()
                this._leave(() => this.onPlay())
            }
            if (e.code === 'KeyM') this.toggleMute()
            if (e.code === 'Escape' || e.code === 'KeyS') this.skipIntro()
        }
        window.addEventListener('keydown', this._onKey)
    }

    async _tryStartMusic() {
        this.phase = 'menu'
        // 1) tenta arquivo real
        const tried = await this._tryHtmlAudio('/menu/music/intro.mp3')
        if (tried) return
        // 2) fallback procedural
        try {
            await this._startProcedural()
        } catch (_) {
            this.playing = false
            this._updateMusicUi()
        }
    }

    async _tryHtmlAudio(url) {
        try {
            const a = new Audio(url)
            a.loop = true
            a.volume = this.volume
            a.preload = 'auto'
            // probe se existe
            await new Promise((resolve, reject) => {
                const t = setTimeout(() => reject(new Error('timeout')), 8000) // 8s: 4.8MB em 3G (era 1.2s → cedia pro procedural)
                const ok = () => { clearTimeout(t); resolve() }
                const err = () => { clearTimeout(t); reject() }
                a.addEventListener('canplay', ok, { once: true })
                a.addEventListener('error', err, { once: true })
                a.load()
            })
            await a.play()
            this.htmlAudio = a
            this.playing = true
            this._updateMusicUi()
            return true
        } catch (_) {
            if (this.htmlAudio) {
                try { this.htmlAudio.pause() } catch (__) {}
                this.htmlAudio = null
            }
            return false
        }
    }

    async _startProcedural() {
        const Ctx = window.AudioContext || window.webkitAudioContext
        if (!Ctx) return
        this.audioCtx = new Ctx()
        if (this.audioCtx.state === 'suspended') await this.audioCtx.resume()

        this.musicGain = this.audioCtx.createGain()
        this.musicGain.gain.value = this.muted ? 0 : this.volume * 0.35
        this.musicGain.connect(this.audioCtx.destination)

        // bass pulse + lead arpeggio (arcade/racing vibe)
        const t0 = this.audioCtx.currentTime + 0.05
        this._schedProcedural(t0)
        this.playing = true
        this._updateMusicUi()

        // re-schedule loop every ~4 bars
        this._procTimer = setInterval(() => {
            if (!this.audioCtx || this.muted) return
            this._schedProcedural(this.audioCtx.currentTime + 0.05)
        }, 3200)
    }

    _schedProcedural(t0) {
        if (!this.audioCtx || !this.musicGain) return
        const ctx = this.audioCtx
        const tempo = 0.18 // beat

        // kick / bass
        for (let i = 0; i < 16; i++) {
            const t = t0 + i * tempo
            const o = ctx.createOscillator()
            const g = ctx.createGain()
            o.type = 'sine'
            o.frequency.setValueAtTime(i % 4 === 0 ? 90 : 55, t)
            g.gain.setValueAtTime(0.0001, t)
            g.gain.exponentialRampToValueAtTime(0.5, t + 0.02)
            g.gain.exponentialRampToValueAtTime(0.0001, t + 0.15)
            o.connect(g)
            g.connect(this.musicGain)
            o.start(t)
            o.stop(t + 0.16)
        }

        // neon lead (minor pentatonic-ish)
        const notes = [220, 261.63, 293.66, 329.63, 392, 440, 392, 329.63]
        for (let i = 0; i < notes.length; i++) {
            const t = t0 + i * tempo * 2
            const o = ctx.createOscillator()
            const g = ctx.createGain()
            o.type = 'square'
            o.frequency.value = notes[i]
            g.gain.setValueAtTime(0.0001, t)
            g.gain.exponentialRampToValueAtTime(0.12, t + 0.03)
            g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28)
            const filter = ctx.createBiquadFilter()
            filter.type = 'lowpass'
            filter.frequency.value = 1800
            o.connect(filter)
            filter.connect(g)
            g.connect(this.musicGain)
            o.start(t)
            o.stop(t + 0.3)
        }

        // hi-hat noise ticks
        for (let i = 0; i < 16; i++) {
            if (i % 2 === 0) continue
            const t = t0 + i * tempo
            const bufferSize = 2 * ctx.sampleRate * 0.03
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
            const data = buffer.getChannelData(0)
            for (let j = 0; j < bufferSize; j++) data[j] = (Math.random() * 2 - 1) * 0.4
            const noise = ctx.createBufferSource()
            noise.buffer = buffer
            const g = ctx.createGain()
            g.gain.setValueAtTime(0.08, t)
            g.gain.exponentialRampToValueAtTime(0.0001, t + 0.04)
            noise.connect(g)
            g.connect(this.musicGain)
            noise.start(t)
            noise.stop(t + 0.05)
        }
    }

    _applyVolume() {
        if (this.htmlAudio) this.htmlAudio.volume = this.muted ? 0 : this.volume
        if (this.musicGain && this.audioCtx) {
            this.musicGain.gain.setTargetAtTime(
                this.muted ? 0 : this.volume * 0.35,
                this.audioCtx.currentTime,
                0.05
            )
        }
    }

    toggleMute() {
        this.muted = !this.muted
        this._applyVolume()
        this._updateMusicUi()
        // se nunca tocou (autoplay bloqueado), tenta de novo no gesto
        if (!this.playing && !this.muted) this._tryStartMusic()
    }

    skipIntro() {
        // para a música da intro mas mantém o menu (usuário pediu pular música)
        this.stopMusic()
        this.musicLabel.textContent = 'Intro pulada'
        this.musicIcon.textContent = '🔇'
    }

    stopMusic() {
        this.playing = false
        if (this._procTimer) {
            clearInterval(this._procTimer)
            this._procTimer = null
        }
        if (this.htmlAudio) {
            try { this.htmlAudio.pause() } catch (_) {}
            this.htmlAudio = null
        }
        if (this.audioCtx) {
            try { this.audioCtx.close() } catch (_) {}
            this.audioCtx = null
            this.musicGain = null
        }
        this._updateMusicUi()
    }

    _updateMusicUi() {
        if (!this.musicLabel) return
        if (!this.playing) {
            this.musicIcon.textContent = '🔇'
            this.musicLabel.textContent = this.muted ? 'Mudo' : 'Tocar música'
        } else if (this.muted) {
            this.musicIcon.textContent = '🔇'
            this.musicLabel.textContent = 'Mudo'
        } else {
            this.musicIcon.textContent = '♪'
            this.musicLabel.textContent = 'Tocando'
        }
    }

    _leave(cb) {
        if (this.phase === 'leaving') return
        this.phase = 'leaving'
        this.root.classList.add('mm-out')
        // fade out music
        if (this.musicGain && this.audioCtx) {
            this.musicGain.gain.setTargetAtTime(0, this.audioCtx.currentTime, 0.15)
        }
        if (this.htmlAudio) {
            const a = this.htmlAudio
            const step = () => {
                a.volume = Math.max(0, a.volume - 0.05)
                if (a.volume > 0.01) requestAnimationFrame(step)
                else this.stopMusic()
            }
            step()
        } else {
            setTimeout(() => this.stopMusic(), 400)
        }
        setTimeout(() => {
            this.destroy()
            cb()
        }, 480)
    }

    destroy() {
        window.removeEventListener('keydown', this._onKey)
        this.stopMusic()
        this.root?.remove()
        this.root = null
    }
}
